/* ============================================================
   ZNAKI · audio/background.js
   Tło instalacji — sekwencja czterech spokojnych akordów
   (tlo_1 → tlo_2 → tlo_3 → tlo_4 → tlo_1 …).

   Zasada działania (wg koncepcji autora dźwięku):
     · zawsze gra dokładnie jeden akord — tło nigdy się nie zatrzymuje,
       zmienia się tylko jego poziom;
     · poziom idzie za prędkością przewijania tablicy: ruch = tło słychać,
       bezruch (i "ściśnięcie" figur przytrzymaniem) = tło gaśnie w 1–2 s;
     · zatrzymanie tablicy NIE przełącza akordu od razu — tylko odkłada
       zmianę; następny akord startuje (od swojego początku) dopiero
       wtedy, gdy tablica znów rusza, czyli dokładnie w chwili powrotu
       tła. Postój liczy się zaś dopiero wtedy, gdy tło zdążyło
       wybrzmieć (bgQuiet) — dzięki temu przerwa między machnięciami
       kółka nie zmienia harmonii w środku przewijania;
     · akord trwa ~38 s; jeśli ruch trwa dłużej, plik jest przedłużany
       przez nałożenie go na siebie z kilkusekundową zakładką
       (Voice, tryb overlap).
   ============================================================ */

import { PARAMS } from '../config';
import { clamp01, lerp } from '../utils';
import { FILTER_OPEN } from './chain';

class BackgroundLayer {

  /**
   * @param {AudioEngine} engine
   * @param {String[]} urls — akordy w kolejności odtwarzania
   */
  constructor(engine, urls) {
    this.engine = engine;
    this.urls = urls || [];

    /* magistrala tła bywa dziedziczona po poprzednim wejściu na widok —
       zaczynamy zawsze od ciszy */
    this.bus = engine.bus('bg', { volume: 0 });
    if (this.bus) this.bus.cancelRamps().setVolume(0, 0);

    this.index = 0;         /* który akord gra teraz */
    this.level = 0;         /* wygładzona aktywność tablicy 0–1 */

    this._voice = null;
    this._retiring = [];
    this._armed = false;    /* tablica jedzie (od ostatniego zaliczonego postoju) */
    this._pending = false;  /* postój zaliczony — najbliższy ruch odpali kolejny akord */
    this._stallT = 0;       /* jak długo trwa bezruch [s] */
    this._lastSwapAt = -1e9;
    this._pauseTimer = null;
    this._paused = false;
    this._started = false;
    this._destroyed = false;
  }

  /* --- start --------------------------------------------------- */

  /**
   * Wczytaj próbki i uruchom pierwszy akord, gdy tylko kontekst ruszy.
   * @return {Promise} spełniona, gdy całe tło jest w cache
   */
  start() {
    if (!this.urls.length || this._started || !this.engine.supported) {
      return Promise.resolve();
    }
    this._started = true;

    /* pierwszy akord ma priorytet, reszta dogrywa się w tle */
    return this.engine.load(this.urls[0])
      .then(() => {
        this.engine.onUnlocked(() => this._playCurrent(0));
        return this.engine.loadSequential(this.urls.slice(1));
      })
      .catch(() => { /* brak dźwięku nie może zatrzymać instalacji */ });
  }

  /* --- klatka -------------------------------------------------- */

  /**
   * @param {object} st — stan chwili z pętli instalacji (index.js)
   * @param {number} dt — czas klatki [s]
   */
  update(st, dt) {
    if (this._destroyed || !this.bus || this._paused) return;
    const A = PARAMS.audio;

    /* aktywność: prędkość przewijania, wygaszana przez "przytrzymanie"
       (moment ściśnięcia figur — wtedy tło ma milczeć, wg noty autora:
       ściśnięcie inicjalizuje wyciszenie, a powrót ruchu — kolejny akord) */
    const speed = Math.abs(st.velSmooth) / Math.max(0.05, A.bgVelRef);
    const drive = clamp01(speed) * (1 - clamp01(st.hold));

    /* narastanie szybkie, opadanie wolne — zgrane z zatrzymaniem tablicy */
    const tc = drive > this.level ? A.bgAttack : A.bgRelease;
    this.level += (drive - this.level) * (1 - Math.exp(-dt / Math.max(0.01, tc)));

    /* poziom magistrali tła (głośność ogólna instalacji siedzi na masterze) */
    const vol = A.bgVolume * lerp(A.bgVolMin, A.bgVolMax, this.level);
    this.bus.setVolume(vol, 0.05);

    /* opcjonalne przymknięcie barwy przy bezruchu (domyślnie wyłączone) */
    if (A.bgFilter > 0) {
      const closed = lerp(A.bgCutoffMin, FILTER_OPEN, this.level);
      this.bus.setCutoff(lerp(FILTER_OPEN, closed, clamp01(A.bgFilter)), 0.1);
    } else {
      this.bus.setCutoff(0, 0.1);
    }

    /* Zmiana akordu jest zsynchronizowana z RUSZENIEM tablicy, nie z jej
       zatrzymaniem: postój tylko odkłada zmianę (_pending), a nowy akord
       wchodzi na zboczu — w chwili, gdy tło i tak wraca, więc zawsze
       słychać go od początku pliku. Postój zalicza się dopiero, gdy tło
       wybrzmiało (level <= bgQuiet); krótka przerwa w przewijaniu zostawia
       poziom wysoko, więc nie przełącza harmonii w środku ruchu. */
    if (drive > A.bgArmLevel) {
      this._stallT = 0;
      if (!this._armed) {
        this._armed = true;
        /* nieudana próba (nie wczytany plik, bgMinChord) zostawia zmianę
           odłożoną — pójdzie po następnym postoju, a nie w środku ruchu */
        if (this._pending && this._advance()) this._pending = false;
      }
    } else if (this._armed) {
      this._stallT += dt;
      if (this._stallT >= A.bgStallTime && this.level <= A.bgQuiet) {
        this._armed = false;
        this._stallT = 0;
        this._pending = true;
      }
    }
  }

  /* --- sterowanie ---------------------------------------------- */

  /** wyjście z widoku instalacji — wycisz i uśpij kontekst */
  pause(fade = 0.4) {
    if (this._paused || !this.bus) return;
    this._paused = true;
    this.bus.fade(0, fade);
    clearTimeout(this._pauseTimer);
    this._pauseTimer = setTimeout(() => {
      if (this._paused && !this._destroyed) this.engine.suspend();
    }, (fade + 0.05) * 1000);
  }

  resume() {
    if (!this._paused) return;
    clearTimeout(this._pauseTimer);
    this._pauseTimer = null;
    this._paused = false;
    this.level = 0;
    if (this.bus) this.bus.cancelRamps();   /* przerwij niedokończone wyciszanie */
    this.engine.resume();
  }

  destroy() {
    this._destroyed = true;
    clearTimeout(this._pauseTimer);   /* inaczej uśpiłby kontekst po ponownym wejściu */
    this._pauseTimer = null;
    if (this.bus) this.bus.cancelRamps().setVolume(0, 0);
    if (this._voice) this._voice.destroy();
    this._retiring.forEach((v) => v.destroy());
    this._retiring.length = 0;
    this._voice = null;
  }

  get state() {
    return {
      index: this.index,
      level: this.level,
      running: !!this._voice,
      pending: this._pending,
    };
  }

  /* --- wewnętrzne ---------------------------------------------- */

  /* następny akord wchodzi na wygasający poprzedni
     @return {Boolean} czy akord faktycznie się zmienił */
  _advance() {
    if (!this.urls.length) return false;
    const A = PARAMS.audio;
    if (this.engine.ctx.currentTime - this._lastSwapAt < A.bgMinChord) return false;

    const next = (this.index + 1) % this.urls.length;
    const url = this.urls[next];

    /* akord jeszcze się nie wczytał — zostajemy przy bieżącym, spróbujemy
       przy kolejnym zatrzymaniu */
    if (!this.engine.getBuffer(url)) {
      this.engine.load(url).catch(() => {});
      return false;
    }

    this.index = next;
    this._playCurrent(A.bgFadeIn);
    return true;
  }

  /* Uruchom akord o bieżącym indeksie, wycofując poprzedni.
     Krzyżowanie akordów jest z założenia bardzo krótkie (bgFadeIn/bgFadeOut
     rzędu 0,1 s): zmiana wypada w chwili ruszenia tablicy, gdy magistrala tła
     jest jeszcze przy zerze, więc całe narastanie robi poziom (bgAttack).
     Dłuższe zbocza przeniosłyby przejście w głośną fazę tuż po ruszeniu —
     najpierw słychać stary akord, a dopiero po chwili nowy. */
  _playCurrent(fadeIn) {
    if (this._destroyed) return;
    const A = PARAMS.audio;
    const buffer = this.engine.getBuffer(this.urls[this.index]);
    if (!buffer) return;

    this._lastSwapAt = this.engine.ctx.currentTime;
    if (this._voice) this._retire(this._voice, A.bgFadeOut);

    this._voice = this.engine.play(buffer, {
      bus: this.bus,
      volume: 1,                 /* poziom niesie magistrala tła */
      overlap: A.bgOverlap,      /* przedłużanie akordu zakładką */
      fadeIn: fadeIn || 0,
    });
  }

  _retire(voice, fade) {
    voice.stop(fade);
    this._retiring.push(voice);
    setTimeout(() => {
      const i = this._retiring.indexOf(voice);
      if (i >= 0) this._retiring.splice(i, 1);
    }, (fade + 0.3) * 1000);
  }
}

export default BackgroundLayer;
